import { config } from "../Environment";

export interface CreateCheckpointReminderRequest {
  processId?: string; // Required for resume_engagement
  prescriptionId?: string; // Required for start_new_engagement
  scheduledDateTime: string; // ISO format
  activityTitle: string;
  actionItems?: string[];
  insights?: string[];
  reminderAction?: "resume_engagement" | "start_new_engagement";
}

export interface CheckpointReminder {
  _id: string;
  fiUser: string;
  fiProcess: string;
  fiPrescription?: string;
  reminderType: "author_default" | "user_custom";
  reminderAction: "resume_engagement" | "start_new_engagement";
  scheduledDateTime: string;
  time: string;
  frequency: "once" | "daily" | "weekly" | "biweekly" | "monthly";
  status: "pending" | "processing" | "sent" | "failed" | "cancelled";
  activityTitle: string;
  processUrl?: string;
  userEmail: string;
  emailContent?: {
    processUrl?: string;
    actionItems?: string[];
    insights?: string[];
    activityContext?: string;
  };
  sentAt?: string;
  lastSentAt?: string;
  sentCount: number;
  failureReason?: string;
  retryCount: number;
  cancelledReason?: string;
  createdOn: string;
  createdBy: "system" | "user";
  lastUpdated: string;
  archived: boolean;
}

export interface CreateCheckpointReminderResponse {
  success: boolean;
  reminderId: string;
  scheduledDateTime: string;
  reminderAction: "resume_engagement" | "start_new_engagement";
}

export interface GetRemindersResponse {
  success: boolean;
  count: number;
  reminders: CheckpointReminder[];
}

export interface GetActiveReminderResponse {
  success: boolean;
  hasActiveReminder: boolean;
  reminder: CheckpointReminder | null;
}

/**
 * Create a new checkpoint reminder for the authenticated user
 */
export const createCheckpointReminder = async (
  retryFetch: Function,
  data: CreateCheckpointReminderRequest
): Promise<CreateCheckpointReminderResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/checkpoint-reminder`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to create reminder: ${response.statusText}`);
  }

  const result: CreateCheckpointReminderResponse = await response.json();
  return result;
};

/**
 * Get all reminders for the authenticated user
 */
export const getMyReminders = async (
  retryFetch: Function,
  options?: {
    status?: "pending" | "processing" | "sent" | "failed" | "cancelled";
    includeArchived?: boolean;
  }
): Promise<GetRemindersResponse> => {
  const queryParams = new URLSearchParams();
  if (options?.status) {
    queryParams.append("status", options.status);
  }
  if (options?.includeArchived !== undefined) {
    queryParams.append("includeArchived", String(options.includeArchived));
  }

  const url = `${config.API_BASE}/api/my-reminders${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await retryFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to fetch reminders: ${response.statusText}`);
  }

  const result: GetRemindersResponse = await response.json();
  return result;
};

/**
 * Get reminders for a specific process
 */
export const getProcessReminders = async (
  retryFetch: Function,
  processId: string
): Promise<GetRemindersResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/checkpoint-reminders/${processId}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to fetch process reminders: ${response.statusText}`);
  }

  const result: GetRemindersResponse = await response.json();
  return result;
};

/**
 * Cancel a checkpoint reminder
 */
export const cancelReminder = async (
  retryFetch: Function,
  reminderId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/checkpoint-reminder/${reminderId}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to cancel reminder: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
};

/**
 * Update a checkpoint reminder
 */
export const updateCheckpointReminder = async (
  retryFetch: Function,
  reminderId: string,
  data: { scheduledDateTime: string }
): Promise<{ success: boolean; message: string }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/checkpoint-reminder/${reminderId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to update reminder: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
};

/**
 * Enable default reminder from prescription settings
 */
export const enableDefaultReminder = async (
  retryFetch: Function,
  processId: string,
  activityTitle?: string
): Promise<{ success: boolean; message: string; reminder: any }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/checkpoint-reminder/enable-default/${processId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ activityTitle }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to enable reminder: ${response.statusText}`);
  }

  const result = await response.json();
  return result;
};

/**
 * Get active reminder for a specific process
 */
export const getActiveReminderForProcess = async (
  retryFetch: Function,
  processId: string
): Promise<GetActiveReminderResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/checkpoint-reminder/active/${processId}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.error || `Failed to fetch active reminder: ${response.statusText}`
    );
  }

  const result: GetActiveReminderResponse = await response.json();
  return result;
};
